import React from 'react';
import styled from 'styled-components';
import i18n from '@app/i18n';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseResult } from '@app/components/common/BaseResult/BaseResult';
import { HOME_PATH } from '@app/constants/routerPaths';
import { BaseText } from '@app/components/common/BaseText/BaseText';
import { resolveTranslation } from '@app/utils/translateI18nMessage';
import { formatReferenceLabel } from '@app/utils/notifyApiError';
import { captureRenderError } from '@app/services/sentryService';

const Wrapper = styled.div<{ $fullHeight: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* No background-color: the content background shows through. */
  min-height: ${(props) => (props.$fullHeight ? '100vh' : '16rem')};
  height: ${(props) => (props.$fullHeight ? 'auto' : '100%')};
`;

/* BaseButton is `display: flex`, so it stacks inside antd's `.ant-result-extra`. */
const Actions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Reference = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 1rem;
  word-break: break-all;
`;

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Only the app-level boundary should take the whole viewport. */
  fullHeight?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** Sentry event id; absent when Sentry is disabled. */
  eventId?: string;
}

/**
 * Catches render-time exceptions. Mounted around the routed page in `MainLayout`
 * so a broken page leaves the navigation usable, and at the app root as a last
 * resort for a crash in the layout shell itself.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] render failed:', error, errorInfo.componentStack);
    const eventId = captureRenderError(error, errorInfo.componentStack);
    if (eventId) {
      this.setState({ eventId });
    }
  }

  private handleCopyReference = (): void => {
    if (this.state.eventId) {
      navigator.clipboard?.writeText(this.state.eventId).catch(() => undefined);
    }
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  // `window.location`, not `useNavigate`: the app-level boundary sits outside
  // `BrowserRouter`, so a router hook would throw here.
  private handleGoHome = (): void => {
    window.location.assign(HOME_PATH);
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // `i18n.t` rather than a hook: class component, and translations may not have loaded.
    const { t } = i18n;
    const title = resolveTranslation(t, 'common.oops');
    const subTitle = resolveTranslation(t, 'common.somethingWentWrong');
    const reloadLabel = resolveTranslation(t, 'common.reload');
    const homeLabel = resolveTranslation(t, 'error404.comeBack');
    const copyLabel = resolveTranslation(t, 'common.copy');
    const { eventId } = this.state;

    return (
      <Wrapper $fullHeight={this.props.fullHeight ?? false}>
        <BaseResult
          status="error"
          title={title}
          subTitle={subTitle}
          extra={
            <>
              <Actions>
                <BaseButton type="primary" onClick={this.handleReload}>
                  {reloadLabel}
                </BaseButton>
                <BaseButton onClick={this.handleGoHome}>{homeLabel}</BaseButton>
              </Actions>
              {eventId && (
                <Reference>
                  <BaseText color="light">{formatReferenceLabel(t, eventId)}</BaseText>
                  <BaseButton type="text" size="small" onClick={this.handleCopyReference}>
                    {copyLabel}
                  </BaseButton>
                </Reference>
              )}
            </>
          }
        />
      </Wrapper>
    );
  }
}
